import type { AccessTokenPayload, UserPersonalDetails } from "roo-shared";
import { sharedUniversal } from "roo-shared";
import { RooDate } from "roo-time";
import { SDK, registerAuthHooks } from "./sdk.service.js";
import * as tokenStore from "./tokenStore.js";

/**
 * Session state + token lifecycle for the web app.
 *
 * The token PROTOCOL is shared with mobile — same endpoints, same sliding rotation. What differs is
 * that the refresh token is never in JS's hands: it rides an HttpOnly cookie the browser attaches
 * automatically, so this file never reads, stores, or sends one. It only asks the server to spend it.
 *
 * Consequences that look like omissions but are the point:
 *   - No `refreshToken` argument anywhere. The server reads the cookie.
 *   - No client-side refresh-token expiry check. We can't decode a token we can't read, so "is the
 *     session still alive?" is answered by the server's response, not by local inspection.
 *   - No client-driven rotation schedule. `refreshAccessToken` is the hot path; rotation is the
 *     server's business.
 *
 * Note the JWT payload uses `expMs` (epoch MILLISECONDS), not the JWT-standard `exp` (seconds) —
 * see roo-shared's `RootToken`. Comparisons are against `RooDate.now()` directly.
 */

export type SessionState =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "signedIn"; user: UserPersonalDetails }
  /**
   * Signed out AND we owe the user an acknowledgement that their account was deleted.
   *
   * A distinct state rather than a flag beside `signedOut`, because it IS a distinct situation:
   * "you're logged out" and "your account no longer exists" want different words on screen. It also
   * has to live above the page tree — the dialog that triggers deletion is rendered by
   * `SignedInPage`, which unmounts the instant the session ends, so it cannot show its own
   * confirmation. Anything it set locally would vanish with it.
   */
  | { status: "accountDeleted" };

let state: SessionState = { status: "loading" };
const listeners = new Set<() => void>();

function setState(next: SessionState): void {
  state = next;
  listeners.forEach((l) => l());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): SessionState {
  return state;
}

/** Treat an access token as expired slightly early so it can't die in flight. */
const EXPIRY_SKEW_MS = 30_000;

function isAccessTokenLive(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = sharedUniversal.decodeTokenPayloadWithoutVerification(token, "r-dSoLJUKD") as AccessTokenPayload;
    return payload.expMs > RooDate.now() + EXPIRY_SKEW_MS;
  } catch {
    return false;
  }
}

/**
 * Collapses concurrent refreshes onto one in-flight request. Without this, a page firing several
 * queries on mount would race N refreshes against the same cookie.
 */
let inFlightRefresh: Promise<string | null> | null = null;

/**
 * The single token accessor the SDK calls before every authenticated request. Returns a live access
 * token, minting one from the refresh COOKIE when needed. Null means no usable session.
 */
async function getApiToken(): Promise<string | null> {
  const accessToken = tokenStore.getAccessToken();
  if (isAccessTokenLive(accessToken)) return accessToken;

  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      // No token argument — the browser attaches the HttpOnly cookie. A caller with no cookie gets
      // a definitive failure here, which is exactly how we learn the session is gone.
      const result = await SDK.auth.refreshAccessToken({});
      if (result.status !== "success") {
        endSession();
        return null;
      }
      tokenStore.setAccessToken(result.accessToken);
      return result.accessToken;
    } catch {
      // A network blip must not log the user out — leave the session intact and let the caller's
      // request fail. Only a definitive rejection above ends the session.
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/** Local teardown only. The server-side cookie clear is `logout()`. */
function endSession(): void {
  tokenStore.clearAccessToken();
  setState({ status: "signedOut" });
}

/**
 * Adopt a freshly minted session. Every endpoint that signs a user in — email code, signup, and
 * each social path — lands here, so there is one place that knows what a web session consists of:
 * the access token in memory, and nothing else. `refreshToken` is deliberately never read; for
 * `client: "web"` the server returns it empty and puts the real token in the cookie.
 */
function startSession(p: { accessToken: string; user: UserPersonalDetails }): void {
  tokenStore.setAccessToken(p.accessToken);
  setState({ status: "signedIn", user: p.user });
}

/** Mirrors how mobile sources these from the device. */
function browserLocale(): string {
  return navigator.language || "en-US";
}

/**
 * The timezone matters beyond display: the server keys the card-reminder cron off
 * `communication_timezone`.
 */
function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Denver";
}

/**
 * Wire the SDK and restore any existing session. Called once, before the app renders.
 *
 * Unlike the pre-cookie build there is no local token to inspect first — we cannot see the cookie,
 * so the only way to know whether a session exists is to ask. One authenticated call answers both
 * "is the cookie still good?" and "who is this?".
 */
export async function initSession(): Promise<void> {
  registerAuthHooks({ getApiToken, onUnauthorized: endSession });

  try {
    const user = await SDK.user.getUserPersonalDetails({});
    setState({ status: "signedIn", user });
  } catch {
    endSession();
  }
}

export type RequestCodeResult = { ok: true } | { ok: false; message: string };

const LOGIN_STEP_1_MESSAGES: Record<string, string> = {
  UNABLE_TO_FIND_USER: "We couldn't find an account with that email address.",
};

const LOGIN_STEP_2_MESSAGES: Record<string, string> = {
  UNABLE_TO_FIND_USER: "We couldn't find an account with that email address.",
  SKIPPED_STEP_1: "That code has expired. Request a new one.",
  INCORRECT_CODE: "That code isn't right. Check it and try again.",
  EXPIRED_CODE: "That code has expired. Request a new one.",
};

/** Step 1 — ask the server to email a verification code. */
export async function requestLoginCode(emailRaw: string): Promise<RequestCodeResult> {
  const result = await SDK.user.loginStep1({ emailRaw });
  if (result.status === "success") return { ok: true };
  return { ok: false, message: LOGIN_STEP_1_MESSAGES[result.errorCode] ?? "We couldn't send a code. Try again." };
}

/**
 * Step 2 — exchange the code for a session.
 *
 * `client: "web"` is what makes the server issue a 28-day HttpOnly cookie instead of returning a
 * 180-day token in the body. Omitting it would silently get mobile's posture, which is the whole
 * thing this app is avoiding — so it is not optional at this call site.
 */
export async function submitLoginCode(emailRaw: string, verificationCode: string): Promise<RequestCodeResult> {
  const result = await SDK.user.loginStep2({ emailRaw, verificationCode, client: "web" });
  if (result.status !== "success") {
    return { ok: false, message: LOGIN_STEP_2_MESSAGES[result.errorCode] ?? "We couldn't sign you in. Try again." };
  }
  // `result.refreshToken` is deliberately an empty string for web — the real token went into the
  // cookie and never touches JS. Only the access token is kept, in memory.
  startSession(result);
  return { ok: true };
}

const SIGN_UP_MESSAGES: Record<string, string> = {
  USER_ALREADY_EXISTS: "An account with that email already exists. Try logging in instead.",
  INVALID_EMAIL: "That doesn't look like a valid email address.",
};

/**
 * Create an account. Signup mints a session exactly like login does, so it carries the same
 * `client: "web"` flag — without it the browser would be handed a 180-day token in JS and no
 * cookie, silently bypassing the storage model the login path goes through.
 *
 * Locale and timezone come from the browser, mirroring how mobile sources them from the device.
 * The timezone matters beyond display: the server keys the card-reminder cron off
 * `communication_timezone`.
 */
export async function signUp(p: { emailRaw: string; firstName: string; lastName: string }): Promise<RequestCodeResult> {
  const result = await SDK.user.signUp({
    emailRaw: p.emailRaw,
    firstName: p.firstName,
    lastName: p.lastName,
    communicationLocale: browserLocale(),
    communicationTimezone: browserTimezone(),
    client: "web",
  });
  if (result.status !== "success") {
    return { ok: false, message: SIGN_UP_MESSAGES[result.errorCode] ?? "We couldn't create your account. Try again." };
  }
  // As with login, `result.refreshToken` is deliberately empty for web — the real token went into
  // the cookie. Only the access token is kept, in memory.
  startSession(result);
  return { ok: true };
}

/**
 * The outcome of exchanging a provider ID token for a session.
 *
 * `needsSignUp` is its own outcome rather than an error message because it is a ROUTING decision,
 * not a failure: the Login page sends the user to SignUp, which offers the same button against the
 * signup endpoint. Mirrors mobile's `onNeedsSignup` callback.
 */
export type SocialAuthResult = { ok: true } | { ok: false; needsSignUp: boolean; message: string };

const NO_ACCOUNT_MESSAGE = "We couldn't find a Roo account for that email. Create one to get started.";

/**
 * The provider verified someone, but released no email address — so we have nothing to identify a
 * Roo account by. Its own outcome rather than the generic failure because it is not retryable: the
 * fix is in the user's provider settings, not in pressing the button again.
 */
const noEmailMessage = (provider: "Google" | "Apple") =>
  `Your ${provider} account didn't share an email address, so we can't sign you in with it.`;

/**
 * The provider released an address it says it has NOT verified, and a Roo account already exists on
 * that address — so the server refuses to hand it over on the provider's word alone. The way in is
 * the email code, which only reaches whoever actually owns the inbox.
 */
const emailNotVerifiedMessage = (provider: "Google" | "Apple") =>
  `${provider} hasn't verified that email address, so we can't use it to reach an existing Roo account. Sign in with your email above instead.`;

/**
 * Log in with a Google ID token from `socialAuth.service`.
 *
 * `client: "web"` is what makes the server issue a 28-day HttpOnly cookie instead of returning a
 * 180-day token in the body. Omitting it would silently get mobile's posture, which is the whole
 * thing this app is avoiding — so it is not optional at this call site, just as it isn't for
 * `loginStep2`.
 */
export async function loginWithGoogle(identityToken: string): Promise<SocialAuthResult> {
  const result = await SDK.user.loginWithGoogle({ identityToken, client: "web" });
  if (result.status === "success") {
    startSession(result);
    return { ok: true };
  }
  if (result.errorCode === "UNABLE_TO_FIND_USER") {
    return { ok: false, needsSignUp: true, message: NO_ACCOUNT_MESSAGE };
  }
  // Deliberately NOT `needsSignUp` — routing to a signup page that will fail for the same reason
  // would just move the dead end one screen further along.
  if (result.errorCode === "NO_EMAIL") {
    return { ok: false, needsSignUp: false, message: noEmailMessage("Google") };
  }
  if (result.errorCode === "EMAIL_NOT_VERIFIED") {
    return { ok: false, needsSignUp: false, message: emailNotVerifiedMessage("Google") };
  }
  return { ok: false, needsSignUp: false, message: "We couldn't sign you in with Google. Try again." };
}

/**
 * Create an account from a Google ID token — or sign in, if one already exists.
 *
 * The endpoint is signup-OR-login (`success_signup` | `success_login`), because a user whose
 * account was created by email code can link Google here. Both exits mint a session, so both carry
 * `client: "web"` and both land in `startSession`.
 *
 * No name fields: Google's ID token carries `given_name` / `family_name` and the server reads them
 * from the verified claims rather than trusting anything we send.
 */
export async function signUpWithGoogle(identityToken: string): Promise<SocialAuthResult> {
  const result = await SDK.user.signUpWithGoogle({
    identityToken,
    communicationLocale: browserLocale(),
    communicationTimezone: browserTimezone(),
    client: "web",
  });
  if (result.status === "failed") {
    return {
      ok: false,
      needsSignUp: false,
      message:
        result.errorCode === "NO_EMAIL"
          ? "Your Google account didn't share an email address, so we can't create an account with it."
          : "We couldn't create your account with Google. Try again.",
    };
  }
  startSession(result);
  return { ok: true };
}

/** Apple's twin of `loginWithGoogle`. Same posture, same routing outcome. */
export async function loginWithApple(identityToken: string): Promise<SocialAuthResult> {
  const result = await SDK.user.loginWithApple({ identityToken, client: "web" });
  if (result.status === "success") {
    startSession(result);
    return { ok: true };
  }
  if (result.errorCode === "UNABLE_TO_FIND_USER") {
    return { ok: false, needsSignUp: true, message: NO_ACCOUNT_MESSAGE };
  }
  // See `loginWithGoogle` — a signup redirect would hit the same wall.
  if (result.errorCode === "NO_EMAIL") {
    return { ok: false, needsSignUp: false, message: noEmailMessage("Apple") };
  }
  if (result.errorCode === "EMAIL_NOT_VERIFIED") {
    return { ok: false, needsSignUp: false, message: emailNotVerifiedMessage("Apple") };
  }
  return { ok: false, needsSignUp: false, message: "We couldn't sign you in with Apple. Try again." };
}

/**
 * Apple's twin of `signUpWithGoogle`, with one asymmetry: the name is passed explicitly.
 *
 * Apple releases the user's name exactly once, on the first authorization, and it is not in the ID
 * token — so unlike Google's endpoint (which reads `given_name` / `family_name` from the verified
 * claims) this one has to be told. Absent on every later sign-in, which is fine: by then the account
 * exists and the `success_login` exit ignores the fields entirely.
 */
export async function signUpWithApple(p: {
  identityToken: string;
  firstName?: string;
  lastName?: string;
}): Promise<SocialAuthResult> {
  const result = await SDK.user.signUpWithApple({
    identityToken: p.identityToken,
    firstName: p.firstName,
    lastName: p.lastName,
    communicationLocale: browserLocale(),
    communicationTimezone: browserTimezone(),
    client: "web",
  });
  if (result.status === "failed") {
    return {
      ok: false,
      needsSignUp: false,
      message:
        result.errorCode === "NO_EMAIL"
          ? "Your Apple account didn't share an email address, so we can't create an account with it."
          : "We couldn't create your account with Apple. Try again.",
    };
  }
  startSession(result);
  return { ok: true };
}

/**
 * Permanently delete the account, then end the session.
 *
 * Server-side this is an erasure that leaves a tombstone and revokes every bank connection, so the
 * UI must confirm hard before calling it — the caller owns that (see `DeleteAccountDialog`).
 *
 * The local teardown deliberately does NOT go through `logout()`: that would call `webLogout` with a
 * token whose user no longer exists. Clearing the cookie is pointless once the account is gone —
 * the token can't authenticate anything — so we just drop local state. Mobile does the same thing
 * via `logout({skipAnonFlush: true})`, for the same reason: a still-decodable token now points at a
 * deleted user.
 */
export async function deleteAccount(): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await SDK.user.deleteUser({});
  } catch {
    return { ok: false, message: "We couldn't delete your account. Try again, or contact support." };
  }
  // Deliberately NOT `endSession()` — that lands on the login form with no acknowledgement, which
  // reads as the app having logged you out rather than as the irreversible thing you just did.
  tokenStore.clearAccessToken();
  setState({ status: "accountDeleted" });
  return { ok: true };
}

/** Dismiss the account-deleted acknowledgement and fall through to the ordinary signed-out view. */
export function dismissAccountDeleted(): void {
  setState({ status: "signedOut" });
}

/**
 * Log out. Unlike mobile's purely local logout this needs a round trip, because only the server can
 * clear an HttpOnly cookie. Local state is cleared regardless of whether that call succeeds — a
 * failed logout must never leave the user looking signed in.
 */
export async function logout(): Promise<void> {
  try {
    await SDK.auth.webLogout({});
  } catch {
    // Network failure: the cookie may survive, but this browser's session is over as far as the UI
    // is concerned, and the next authenticated call will 401 into the same place.
  } finally {
    endSession();
  }
}
